import { prefersReducedMotion } from "../lib/internal_utils";
import { cn } from "../lib/utils";
import React, { useEffect, useMemo, useRef, useState } from "react";

// Matches the `duration-200` of each character's fade-in.
const FADE_IN_MS = 200;
/**
 * Reveals a string one character at a time, each fading in, to mimic live typing. Use it for
 * playful intros, hero text, or to suggest an agent is "typing" a short message, keeping `text`
 * concise so the reveal does not feel slow; for streaming model output that genuinely
 * arrives token-by-token, render the real tokens rather than this decorative effect.
 *
 * @summary Character-by-character typing effect.
 */
export function TypingAnimation({
  text,
  duration = 50,
  className,
  onComplete
}) {
  // Code points, so that surrogate pairs (emoji) are never split.
  const characters = useMemo(() => Array.from(text), [text]);
  const [reduceMotion] = useState(prefersReducedMotion);
  // Start at one to avoid an empty text as first display as it sometimes shrinks the container.
  const initialCount = reduceMotion ? characters.length : Math.min(1, characters.length);
  const [progress, setProgress] = useState({
    text,
    count: initialCount
  });
  // A new `text` restarts the animation rather than showing the previous text's progress.
  const revealedCount = progress.text === text ? progress.count : initialCount;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Read when an animation starts, so changing it neither restarts nor re-completes it.
  const durationMsRef = useRef(duration);
  durationMsRef.current = duration;
  useEffect(() => {
    const durationMs = durationMsRef.current;
    // Keep ticking after the last character is revealed: one tick, or until it has fully faded in.
    const completeCount = characters.length + (reduceMotion ? 1 : Math.ceil(FADE_IN_MS / durationMs));
    let count = initialCount;
    const typingEffect = setInterval(() => {
      count += 1;
      if (count <= characters.length) {
        setProgress({
          text,
          count
        });
      }
      if (count >= completeCount) {
        var _onCompleteRef$curren;
        clearInterval(typingEffect);
        (_onCompleteRef$curren = onCompleteRef.current) === null || _onCompleteRef$curren === void 0 || _onCompleteRef$curren.call(onCompleteRef);
      }
    }, durationMs);
    return () => {
      clearInterval(typingEffect);
    };
  }, [characters, initialCount, reduceMotion, text]);
  return (
    /*#__PURE__*/
    // Keyed by text so that a new text fades in from its first character again.
    React.createElement("span", {
      key: text,
      className: cn("notranslate", className)
    }, characters.slice(0, revealedCount).map((character, index) => /*#__PURE__*/React.createElement("span", {
      key: index,
      className: "animate-in fade-in-0 duration-200 ease-enter motion-reduce:animate-none"
    }, character)))
  );
}
//# sourceMappingURL=TypingAnimation.js.map